// /:partnerId/reset-password?token=... — completa el flow de password reset (2.13).
//
// Llega aca clickeando el link del mail SES. El token vive en el query param;
// el usuario tipea su nueva password 2 veces y le hacemos POST con el token
// + new_password al backend, que verifica el token y setea la password en
// Cognito.
//
// Estados:
//   - editing: form abierto
//   - submitting: enviando
//   - done: password cambiada, link a /login
//   - error: token invalido / vencido / password rechazada

import { FormEvent, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import PasswordChecklist, { PasswordMatchHint } from '@/components/PasswordChecklist';
import { PASSWORD_MIN_LENGTH, isPasswordValid } from '@/lib/password-policy';
import { scrollToField } from '@/lib/dom';
import { completePasswordReset, ApiError } from '@/lib/api';

export default function ResetPassword(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<'password' | 'confirm' | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  // Estado inicial sin token: pantalla de error inmediata.
  if (!token) {
    return (
      <AuthShell
        title="Link inválido"
        subtitle="No encontramos el token en la URL."
        footerLinks={
          <Link to={`/${partnerId}/forgot-password`} className="hover:underline">
            Pedir un nuevo link
          </Link>
        }
      >
        <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
          El link parece estar truncado. Asegurate de copiarlo entero desde el mail.
        </div>
      </AuthShell>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (!isPasswordValid(password)) {
      setErr('La contraseña todavía no cumple todos los requisitos. Revisá la lista debajo del campo.');
      setInvalidField('password');
      scrollToField(passwordRef.current);
      return;
    }
    if (password !== confirm) {
      setErr('Las contraseñas no coinciden.');
      setInvalidField('confirm');
      scrollToField(confirmRef.current);
      return;
    }
    setLoading(true);
    try {
      // token ya validado arriba — TS lo sabe via early return.
      await completePasswordReset(partnerId, token!, password);
      setDone(true);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : 'No pudimos cambiar la password. El link puede estar vencido.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    // No mostramos un link directo al /login porque el usuario llego aca via
    // mail, no via /authorize, asi que no tiene los params OAuth (client_id,
    // redirect_uri, etc) en la URL. Si lo mandaramos a /login, Login.tsx
    // veria los authz params faltantes y mostraria "Link invalido / Faltan
    // parametros de autorizacion" — UX confuso despues de un reset exitoso.
    // En su lugar, le pedimos que vuelva a la app del partner.
    return (
      <AuthShell title="Contraseña actualizada">
        <div role="status" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text space-y-2">
          <p>Listo. Tu contraseña fue actualizada con éxito.</p>
          <p className="text-text-muted text-xs">
            Volvé a la app o sitio donde estabas e iniciá sesión con tu nueva contraseña. El link que usaste ya no sirve.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Nueva contraseña"
      subtitle="Elegí una contraseña nueva para tu cuenta."
    >
      <form onSubmit={onSubmit} aria-busy={loading ? 'true' : undefined} className="space-y-4" noValidate>
        <FormField
          ref={passwordRef}
          name="password"
          type="password"
          label="Nueva contraseña"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          autoFocus
          invalid={invalidField === 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (invalidField === 'password') setInvalidField(null);
          }}
        />
        <PasswordChecklist password={password} />
        <FormField
          ref={confirmRef}
          name="confirm"
          type="password"
          label="Repetí la contraseña"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          invalid={invalidField === 'confirm'}
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            if (invalidField === 'confirm') setInvalidField(null);
          }}
        />
        <PasswordMatchHint password={password} confirm={confirm} />
        {err ? (
          <div role="alert" aria-live="polite" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
            {err}
          </div>
        ) : null}
        <SubmitButton loading={loading} loadingLabel="Cambiando…">
          Cambiar contraseña
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
